import { Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState, useContext, ChangeEvent } from "react";
import { toast } from "react-toastify";
import { AppContext } from "../../../contexts/AppContext";
import { EquationsService } from "../../../services/equations/equationService";
import ImportComponent from "./ImportComponent";
import { downloadJsonFile } from "./util";
import ImageComponent from "./ImageComponent";
import DownloadIcon from '@mui/icons-material/Download';
import { ResultsService } from "../../../services/results/ResultsService";
import GiftCardTracker from "./GiftCardTracker";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

interface Props {
    refreshGame: () => void
}

const AdminPanel = ({ refreshGame }: Props) => {
    const [page, setPage] = useState(0);
    const [exportServer, setExportServer] = useState<string>("ALL");
    const { jwtToken } = useContext(AppContext);

    function a11yProps(index: number) {
        return {
            id: `simple-tab-${index}`,
            'aria-controls': `simple-tabpanel-${index}`,
        };
    }

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setPage(newValue);
    };

    const onDownloadConstantsClick = async () => {
        if (jwtToken) {
            const res = await EquationsService.getConstantsFile(jwtToken);
            downloadJsonFile(res.data, "constants.json");
        }
    }

    const onDownloadGlobalClick = async () => {
        const res = await EquationsService.getGlobalDefaultValues();
        downloadJsonFile(res.data, "global.json");
    }

    const onDownloadParametersClick = async () => {
        if (jwtToken) {
            const res = await EquationsService.getParametersFile(jwtToken);
            // create file link in browser's memory
            const f = new File([res.data], 'file.csv');
            const href = URL.createObjectURL(f);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'file.csv'); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        }
    }

    const onDownloadDefaultsClick = async () => {
        if (jwtToken) {
            const res = await EquationsService.getGameDefaultValuesFile(jwtToken);
            downloadJsonFile(res.data, 'default.json');
        }
    }

    const onDownloadConfigClick = async () => {
        if (jwtToken) {
            const res = await EquationsService.getGameConfigFile(jwtToken);
            downloadJsonFile(res.data, 'config.json');
        }
    }

    const onUploadConstantsClick = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        const file = e.target.files[0];

        if (jwtToken) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                if (!evt?.target?.result) {
                    return;
                }

                const jsonObj = JSON.parse(evt.target.result as string);
                const jsonContent = JSON.stringify(jsonObj);

                const res = await EquationsService.uploadConstantsFile(jwtToken, jsonContent);

                if (res.status === 200) {
                    toast("Fichier téléversé avec succès!");
                    refreshGame()
                } else {
                    toast("Échec de téléversement.");
                }
            }

            reader.readAsText(file);
        }
        e.target.value = ""
    }

    const onUploadDefaultsClick = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        const file = e.target.files[0];

        if (jwtToken ) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                if (!evt?.target?.result) {
                    return;
                }

                const jsonObj = JSON.parse(evt.target.result as string);
                const jsonContent = JSON.stringify(jsonObj);

                const res = await EquationsService.uploadGameDefaultValuesFile(jwtToken, jsonContent);

                if (res.status === 200) {
                    toast("Fichier téléversé avec succès!");
                    refreshGame()
                    console.log("File sent")
                } else {
                    toast("Échec de téléversement.");
                }
            }

            reader.readAsText(file);
        }
        e.target.value = ""
    }

    const onUploadConfigClick = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        const file = e.target.files[0];

        if (jwtToken ) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                if (!evt?.target?.result) {
                    return;
                }

                const jsonObj = JSON.parse(evt.target.result as string);
                const jsonContent = JSON.stringify(jsonObj);



                const res = await EquationsService.uploadGameConfigFile(jwtToken, jsonContent);

                if (res.status === 200) {
                    toast("Fichier téléversé avec succès!");
                    refreshGame()
                } else {
                    toast("Échec de téléversement.");
                }
            }

            reader.readAsText(file);
        }
        e.target.value = ""
    }

    const onUploadParametersClick = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        const file = e.target.files[0];

        if (jwtToken ) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                if (!evt?.target?.result) {
                    return;
                }

                const res = await EquationsService.uploadParametersFile(jwtToken, evt.target.result)

                if (res.status === 200) {
                    toast("Fichier téléversé avec succès!");
                    refreshGame()
                } else {
                    toast("Échec de téléversement.");
                }
            }

            reader.readAsText(file);
        }
        e.target.value = ""
    }

    const onUploadGlobalClick = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) {
            return;
        }

        const file = e.target.files[0];

        if (jwtToken) {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                if (!evt?.target?.result) {
                    return;
                }

                const jsonObj = JSON.parse(evt.target.result as string);
                
                const isValid = "theme" in jsonObj && "home" in jsonObj && "titles" in jsonObj["home"] && "descriptions" in jsonObj["home"]
                    && "bg_one" in jsonObj["theme"] && "bg_two" in jsonObj["theme"] && "color_one" in jsonObj["theme"]
                    && "color_two" in jsonObj["theme"];

                if (!isValid) { toast("Champs manquants."); return }

                const jsonContent = JSON.stringify(jsonObj);

                try {
                    const res = await EquationsService.setGlobalDefaultValues(jwtToken, jsonContent);

                    console.log(res)

                    if (res.status === 200) {
                        toast("Fichier téléversé avec succès!");
                        refreshGame()
                    } else {
                        throw new Error();
                    }
                } catch (err) {
                    toast("Échec de téléversement.");
                }
            }

            reader.readAsText(file);
        }

        e.target.value = ""
    }

    const onUploadAllClick = async (e: ChangeEvent<HTMLInputElement>) => {
        // TODO: Upload to all servers.
        console.log(e)
    }

    const onDownloadUsersClick = async () => {
        if (jwtToken) {
            const res = await ResultsService.getUsers(jwtToken);
            // create file link in browser's memory
            const f = new File([res.data], 'users.csv');
            const href = URL.createObjectURL(f);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'users.csv'); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        }
    }

    const downloadCsv = (data: any, filename: string) => {
        const f = new File([data], filename);
        const href = URL.createObjectURL(f);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
    }

    const filenameSuffix = (server: string) => server === "ALL" ? "" : `_${server.toLowerCase()}`;

    const onDownloadGamesClick = async () => {
        if (jwtToken) {
            const res = await ResultsService.getGames(jwtToken, exportServer);
            downloadCsv(res.data, `games${filenameSuffix(exportServer)}.csv`);
        }
    }

    const onDownloadCompleteGamesClick = async () => {
        if (jwtToken) {
            const res = await ResultsService.getCompleteGames(jwtToken, exportServer);
            downloadCsv(res.data, `games_complete${filenameSuffix(exportServer)}.csv`);
        }
    }

    return (
        <Box sx={{ width: '90vw', margin: 'auto' }}>
            <Paper>
                <Box sx={{ width: '100%', padding: '1em' }}>
                    <b>Configuration</b>
                </Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={page} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label="Accueil" {...a11yProps(0)} />
                        <Tab label="Équations" {...a11yProps(1)} />
                        <Tab label="Jeu" {...a11yProps(2)} />
                        <Tab label="Images" {...a11yProps(3)} />
                        <Tab label="Cartes cadeau" {...a11yProps(4)} />
                        <Tab label="Export" {...a11yProps(5)} />
                    </Tabs>
                </Box>
                <TabPanel value={page} index={0}>
                    <ImportComponent
                        label="Affichage"
                        extension=".json"
                        uploadFn={onUploadGlobalClick}
                        uploadAllFn={onUploadAllClick}
                        downloadFn={onDownloadGlobalClick} />
                </TabPanel>
                <TabPanel value={page} index={1}>
                    <ImportComponent
                        label="Paramètres constants"
                        extension=".json"
                        uploadFn={onUploadConstantsClick}
                        uploadAllFn={onUploadAllClick}
                        downloadFn={onDownloadConstantsClick} />
                    <ImportComponent
                        label="Paramètres temporels"
                        extension=".csv"
                        uploadFn={onUploadParametersClick}
                        uploadAllFn={onUploadAllClick}
                        downloadFn={onDownloadParametersClick} />
                </TabPanel>
                <TabPanel value={page} index={2}>
                    <ImportComponent
                        label="Valeurs par défaut"
                        extension=".json"
                        uploadFn={onUploadDefaultsClick}
                        uploadAllFn={onUploadAllClick}
                        downloadFn={onDownloadDefaultsClick} />
                    <ImportComponent
                        label="Configuration du jeu"
                        extension=".json"
                        uploadFn={onUploadConfigClick}
                        uploadAllFn={onUploadAllClick}
                        downloadFn={onDownloadConfigClick} />
                </TabPanel>
                <TabPanel value={page} index={3}>
                    <ImageComponent />
                </TabPanel>
                <TabPanel value={page} index={4}>
                    <GiftCardTracker />
                </TabPanel>
                <TabPanel value={page} index={5}>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography>Serveur à exporter :</Typography>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Serveur</InputLabel>
                                <Select
                                    value={exportServer}
                                    label="Serveur"
                                    onChange={(e) => setExportServer(e.target.value)}
                                >
                                    <MenuItem value="ALL">Tous (ALPHA + BETA)</MenuItem>
                                    <MenuItem value="ALPHA">ALPHA seulement</MenuItem>
                                    <MenuItem value="BETA">BETA seulement</MenuItem>
                                </Select>
                            </FormControl>
                            <Typography sx={{ color: "text.secondary", fontSize: "0.9em" }}>
                                (s'applique aux téléchargements de jeux ci-dessous)
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography>Télécharger le fichier utilisateur</Typography>
                            <Button variant="contained" endIcon={<DownloadIcon />} onClick={onDownloadUsersClick}>
                                Télécharger
                            </Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography>Télécharger le fichier de jeux (version simple)</Typography>
                            <Button variant="contained" endIcon={<DownloadIcon />} onClick={onDownloadGamesClick}>
                                Télécharger ({exportServer === "ALL" ? "tous" : exportServer})
                            </Button>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography>Télécharger le fichier de jeux (version complete)</Typography>
                            <Button variant="contained" endIcon={<DownloadIcon />} onClick={onDownloadCompleteGamesClick}>
                                Télécharger ({exportServer === "ALL" ? "tous" : exportServer})
                            </Button>
                        </Stack>
                    </Stack>
                </TabPanel>
            </Paper>

        </Box>
    );
};


export default AdminPanel;